import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  useReadUser,
  useBillingPackages,
  useGetOrganizationSubscription,
  useChangePackagePreview,
  useChangePackage,
} from "../services/api";
import PageLoading from "../components/PageLoading";
import PageError from "../components/PageError";
import Page from "../components/Pages/Page";
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Stack,
  Chip,
  Checkbox,
  Divider,
  Alert,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/joy";
import PageHeader from "../components/PageHeader";
import { User } from "../common/types";
import { moduleRegistry } from "../modules/moduleRegistry";
import { BugReport as BugReportIcon } from "@mui/icons-material";
import { isDevFeaturesEnabled } from "../utils/devFeatures";
import { describeErrorBody } from "../utils/useAuthFetch";

interface BillingPackage {
  id: string;
  tier_name: string;
  display_name: string;
  max_users: number | null;
  base_price_monthly: number;
  available_modules: Array<{
    name: string;
    display_name: string;
    price: number;
    limits: Record<string, any>;
    allocation_mode?: string;
    description?: string;
  }>;
  storage_gb_per_user: number;
  tokens_per_user_monthly: number;
  overage_storage_per_gb: number;
  overage_tokens_per_1k: number;
  description: string;
  display_order: number;
}

interface CurrentSubscription {
  id: string;
  package: string;
  tier_name: string;
  enabled_modules: string[];
  current_user_count: number;
  max_users: number | null;
  monthly_cost: number;
  base_price: number;
  module_cost: number;
  status: string;
  cancel_at_period_end: boolean;
  current_period_start: string | null;
  current_period_end: string | null;
  stripe_subscription_id: string | null;
  updated_at: string | null;
}

const ChangePlan: React.FC = () => {
  const readUserAPI = useReadUser();
  const getBillingPackages = useBillingPackages();
  const getOrganizationSubscription = useGetOrganizationSubscription();
  const changePackagePreview = useChangePackagePreview();
  const changePackage = useChangePackage();

  const [user, setUser] = useState<User | null>(null);
  const [packages, setPackages] = useState<BillingPackage[]>([]);
  const [currentSubscription, setCurrentSubscription] =
    useState<CurrentSubscription | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<BillingPackage | null>(
    null,
  );
  const [selectedModules, setSelectedModules] = useState<Set<string>>(
    new Set(),
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [billingNotConfigured, setBillingNotConfigured] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [affectedWorkspaces, setAffectedWorkspaces] = useState<
    Array<{
      id: string;
      name: string;
      enabled_modules: string[];
      removed_modules: string[];
    }>
  >([]);
  const [changingPlan, setChangingPlan] = useState<boolean>(false);
  const [userLimitErrorModalOpen, setUserLimitErrorModalOpen] =
    useState<boolean>(false);
  const [userLimitError, setUserLimitError] = useState<string | null>(null);

  useEffect(() => {
    const initPage = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user data
        const userData = await readUserAPI();
        setUser(userData);

        // Check if user is admin
        if (userData.role_in_org !== "admin") {
          setError("Only organization administrators can change plans.");
          setLoading(false);
          return;
        }

        if (!userData.organization_id) {
          setError("No organization found.");
          setLoading(false);
          return;
        }

        // Fetch current subscription
        const subscriptionData = await getOrganizationSubscription(
          userData.organization_id,
        );
        if (
          !subscriptionData?.subscription ||
          subscriptionData.subscription.status !== "active"
        ) {
          setError("No active subscription found.");
          setLoading(false);
          return;
        }
        setCurrentSubscription(subscriptionData.subscription);

        // Fetch available packages
        const packagesData = await getBillingPackages();
        setPackages(packagesData || []);

        setLoading(false);
      } catch (err: any) {
        console.error("Failed to initialize change plan page:", err);
        if (err?.response?.detail?.code === "billing_not_configured") {
          setBillingNotConfigured(true);
        } else {
          setError(
            describeErrorBody(err?.response) ||
              err.message ||
              "Failed to load subscription details",
          );
        }
        setLoading(false);
      }
    };

    initPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePackageSelect = (pkg: BillingPackage) => {
    setSelectedPackage(pkg);
    // If it's the current package, pre-select current modules; otherwise pre-select all modules
    if (pkg.tier_name === currentSubscription?.tier_name) {
      setSelectedModules(new Set(currentSubscription?.enabled_modules || []));
    } else {
      const moduleNames = pkg.available_modules.map((m) => m.name);
      setSelectedModules(new Set(moduleNames));
    }
  };

  const handleModuleToggle = (moduleName: string) => {
    setSelectedModules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(moduleName)) {
        newSet.delete(moduleName);
      } else {
        newSet.add(moduleName);
      }
      return newSet;
    });
  };

  const getModuleDisplayName = (
    moduleName: string,
    fallbackName: string,
  ): string => {
    const moduleConfig = moduleRegistry[moduleName];
    return moduleConfig?.name || fallbackName;
  };

  const calculateTotal = (): number => {
    if (!selectedPackage) return 0;

    let total = selectedPackage.base_price_monthly;
    selectedPackage.available_modules.forEach((module) => {
      if (selectedModules.has(module.name)) {
        total += module.price;
      }
    });

    return total;
  };

  const handleChangePlan = async () => {
    if (!selectedPackage || !user?.organization_id) return;

    setChangingPlan(true);
    try {
      // First, preview to get affected workspaces
      const previewResult = await changePackagePreview(
        user.organization_id,
        selectedPackage.id,
        Array.from(selectedModules),
      );

      // If there are affected workspaces, show confirmation modal
      // Handle both null and empty array cases
      const affectedWorkspaces = previewResult.affected_workspaces;
      const hasAffectedWorkspaces =
        affectedWorkspaces &&
        Array.isArray(affectedWorkspaces) &&
        affectedWorkspaces.length > 0;

      if (hasAffectedWorkspaces && affectedWorkspaces) {
        setAffectedWorkspaces(affectedWorkspaces);
        setConfirmModalOpen(true);
        setChangingPlan(false);
        return;
      }

      // No affected workspaces, proceed directly
      await proceedWithChange();
    } catch (err: any) {
      console.error("Failed to preview plan change:", err);
      if (err?.response?.detail?.code === "billing_not_configured") {
        setBillingNotConfigured(true);
        setChangingPlan(false);
        return;
      }
      const errorMessage =
        describeErrorBody(err?.response) ||
        err?.message ||
        "Failed to preview plan change. Please try again.";
      setError(`Preview Error: ${errorMessage}`);
      setChangingPlan(false);
    }
  };

  const proceedWithChange = async () => {
    if (!selectedPackage || !user?.organization_id) return;

    setChangingPlan(true);
    try {
      await changePackage(
        user.organization_id,
        selectedPackage.id,
        Array.from(selectedModules),
      );

      // Success - refresh subscription data and navigate back
      const subscriptionData = await getOrganizationSubscription(
        user.organization_id,
      );
      setCurrentSubscription(subscriptionData.subscription);

      // Reset selection
      setSelectedPackage(null);
      setSelectedModules(new Set());
      setConfirmModalOpen(false);
      setAffectedWorkspaces([]);

      // Show success message or navigate
      alert("Plan changed successfully!");
    } catch (err: any) {
      console.error("Failed to change plan:", err);
      if (err?.response?.detail?.code === "billing_not_configured") {
        setBillingNotConfigured(true);
        setConfirmModalOpen(false);
        return;
      }
      const errorMessage =
        describeErrorBody(err?.response) ||
        err?.message ||
        "Failed to change plan. Please try again.";

      // Check if this is a user limit error
      if (
        errorMessage.includes("Remove members before downgrading") ||
        (errorMessage.includes("Package allows") &&
          errorMessage.includes("current org has"))
      ) {
        setUserLimitError(errorMessage);
        setUserLimitErrorModalOpen(true);
        setConfirmModalOpen(false); // Close confirmation modal if open
      } else {
        setError(`Plan Change Error: ${errorMessage}`);
        setConfirmModalOpen(false); // Close modal on error
      }
    } finally {
      setChangingPlan(false);
    }
  };

  if (loading) {
    return <PageLoading />;
  }

  if (billingNotConfigured) {
    return (
      <Page>
        <PageHeader>
          <Typography level="h2">Change Plan</Typography>
        </PageHeader>
        <Alert color="warning" variant="soft" role="status">
          Billing is not configured for this environment. Plan changes are
          unavailable.
        </Alert>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <PageHeader>
          <Typography level="h2">Change Plan</Typography>
        </PageHeader>
        <PageError>{error}</PageError>
      </Page>
    );
  }

  const isCurrentPlan =
    currentSubscription?.tier_name === selectedPackage?.tier_name &&
    JSON.stringify([...selectedModules].sort()) ===
      JSON.stringify([...(currentSubscription?.enabled_modules || [])].sort());

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Change Plan</Typography>
      </PageHeader>

      {/* Current Plan Summary */}
      {currentSubscription && (
        <Box sx={{ mb: 3 }}>
          <Alert color="primary" variant="soft">
            <Stack spacing={1}>
              <Typography level="title-md">Current Plan</Typography>
              <Typography level="body-sm">
                <strong>
                  {packages.find(
                    (pkg) => pkg.tier_name === currentSubscription.tier_name,
                  )?.display_name || currentSubscription.tier_name}
                </strong>{" "}
                - ${currentSubscription.monthly_cost.toFixed(2)}/month
              </Typography>
              {currentSubscription.enabled_modules.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ flexWrap: "wrap", gap: 1 }}
                >
                  {currentSubscription.enabled_modules.map((module) => (
                    <Chip key={module} size="sm" variant="soft" color="primary">
                      {moduleRegistry[module]?.name || module}
                    </Chip>
                  ))}
                </Stack>
              )}
            </Stack>
          </Alert>
        </Box>
      )}

      {!selectedPackage ? (
        // Package selection view
        <Box>
          <Typography level="body-lg" sx={{ mb: 3 }}>
            Select a new plan for your organization.
          </Typography>
          <Stack
            direction="row"
            sx={{
              flexWrap: "wrap",
              gap: 2,
              "& > *": {
                flex: "1 1 280px",
                minWidth: 280,
                maxWidth: 400,
              },
            }}
          >
            {packages.map((pkg) => {
              const isEnterprise = pkg.base_price_monthly === 0;
              const isContactSales = isEnterprise;
              const isCurrent =
                pkg.tier_name === currentSubscription?.tier_name;

              return (
                <Card
                  key={pkg.id}
                  variant="outlined"
                  sx={{
                    width: "100%",
                    cursor: isContactSales ? "default" : "pointer",
                    transition: "all 0.2s",
                    "&:hover": isContactSales
                      ? {}
                      : {
                          boxShadow: "md",
                          borderColor: "primary.500",
                        },
                  }}
                  onClick={
                    isContactSales ? undefined : () => handlePackageSelect(pkg)
                  }
                >
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography level="h2" sx={{ color: "primary.500" }}>
                            {pkg.display_name}
                          </Typography>
                          {isCurrent && (
                            <Chip size="sm" variant="soft" color="success">
                              Current Tier
                            </Chip>
                          )}
                        </Stack>
                      </Box>
                      <Box>
                        {isContactSales ? (
                          <Typography level="title-lg">
                            Contact Sales
                          </Typography>
                        ) : (
                          <Typography level="title-lg">
                            ${pkg.base_price_monthly.toFixed(2)}
                            <Typography
                              level="body-sm"
                              component="span"
                              sx={{ ml: 0.5 }}
                            >
                              /month
                            </Typography>
                          </Typography>
                        )}
                      </Box>
                      <Divider />
                      <Stack spacing={1}>
                        <Typography level="body-sm">
                          <strong>{pkg.max_users || "Unlimited"}</strong> users
                        </Typography>
                        {(pkg.storage_gb_per_user > 0 ||
                          pkg.storage_gb_per_user === -1) && (
                          <Typography level="body-sm">
                            <strong>
                              {pkg.storage_gb_per_user === -1
                                ? "Unlimited"
                                : `${pkg.storage_gb_per_user}GB`}
                            </strong>{" "}
                            storage per user
                          </Typography>
                        )}
                        {(pkg.tokens_per_user_monthly > 0 ||
                          pkg.tokens_per_user_monthly === -1) && (
                          <Typography level="body-sm">
                            <strong>
                              {pkg.tokens_per_user_monthly === -1
                                ? "Unlimited"
                                : pkg.tokens_per_user_monthly.toLocaleString()}
                            </strong>{" "}
                            tokens per user/month
                          </Typography>
                        )}
                      </Stack>
                      {pkg.description && (
                        <Typography
                          level="body-sm"
                          sx={{ color: "text.secondary" }}
                        >
                          {pkg.description}
                        </Typography>
                      )}
                      {isContactSales ? (
                        <Button
                          variant="solid"
                          color="primary"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = "mailto:hello@metalmind.ai";
                          }}
                        >
                          Contact Sales
                        </Button>
                      ) : (
                        <Button variant="solid" color="primary" fullWidth>
                          Select Package
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Box>
      ) : (
        // Module selection and change plan view
        <Box>
          <Button
            variant="plain"
            onClick={() => {
              setSelectedPackage(null);
              setSelectedModules(new Set());
            }}
            sx={{ mb: 2 }}
          >
            ← Back to Packages
          </Button>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Stack spacing={3}>
                <Box>
                  <Typography level="h2" sx={{ color: "primary.500" }}>
                    {selectedPackage.display_name}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography level="title-sm" sx={{ mb: 2 }}>
                    Base Package
                  </Typography>
                  <Stack spacing={1}>
                    <Typography level="body-sm">
                      <strong>
                        ${selectedPackage.base_price_monthly.toFixed(2)}/month
                      </strong>
                    </Typography>
                    <Typography level="body-sm">
                      {selectedPackage.max_users || "Unlimited"} users
                    </Typography>
                    <Typography level="body-sm">
                      {selectedPackage.storage_gb_per_user}GB storage per user
                    </Typography>
                    <Typography level="body-sm">
                      {selectedPackage.tokens_per_user_monthly.toLocaleString()}{" "}
                      tokens per user/month
                    </Typography>
                  </Stack>
                </Box>

                {selectedPackage.available_modules.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography level="title-sm" sx={{ mb: 2 }}>
                        Available Modules
                      </Typography>
                      <Stack spacing={1.5}>
                        {selectedPackage.available_modules.map((module) => (
                          <Box
                            key={module.name}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.5,
                              p: 1.5,
                              borderRadius: "sm",
                              bgcolor: "background.surface",
                            }}
                          >
                            <Checkbox
                              checked={selectedModules.has(module.name)}
                              onChange={() => handleModuleToggle(module.name)}
                              label={
                                <Box sx={{ flex: 1 }}>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Typography
                                      level="body-sm"
                                      sx={{ fontWeight: "md" }}
                                    >
                                      {getModuleDisplayName(
                                        module.name,
                                        module.display_name,
                                      )}
                                    </Typography>
                                    {module.price > 0 && (
                                      <Chip
                                        size="sm"
                                        variant="soft"
                                        color="primary"
                                      >
                                        +${module.price.toFixed(2)}/month
                                      </Chip>
                                    )}
                                  </Stack>
                                </Box>
                              }
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}

                <Divider />

                <Box>
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography level="title-lg">
                        Total Monthly Cost
                      </Typography>
                      <Typography level="h3" sx={{ color: "primary.500" }}>
                        ${calculateTotal().toFixed(2)}
                      </Typography>
                    </Stack>
                    {currentSubscription &&
                      (() => {
                        const delta =
                          calculateTotal() - currentSubscription.monthly_cost;
                        const isIncrease = delta > 0;
                        const isDecrease = delta < 0;

                        if (delta !== 0) {
                          return (
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography
                                level="body-sm"
                                sx={{ color: "text.secondary" }}
                              >
                                Price Change
                              </Typography>
                              <Typography
                                level="body-md"
                                sx={{
                                  fontWeight: "md",
                                  color: isIncrease
                                    ? "warning.500"
                                    : isDecrease
                                      ? "success.500"
                                      : "neutral.500",
                                }}
                              >
                                {isIncrease ? "+" : ""}
                                {delta.toFixed(2)}/month
                              </Typography>
                            </Stack>
                          );
                        }
                        return null;
                      })()}
                  </Stack>
                </Box>

                <Stack spacing={2}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="solid"
                      color="primary"
                      size="lg"
                      fullWidth
                      onClick={handleChangePlan}
                      disabled={
                        selectedModules.size === 0 ||
                        isCurrentPlan ||
                        changingPlan
                      }
                      loading={changingPlan}
                    >
                      {isCurrentPlan
                        ? "No Changes"
                        : changingPlan
                          ? "Changing Plan..."
                          : "Change Plan"}
                    </Button>
                    {isDevFeaturesEnabled() && (
                      <Button
                        variant="outlined"
                        color="warning"
                        size="lg"
                        startDecorator={<BugReportIcon />}
                        onClick={() => {
                          // Simulate the too-many-users error
                          setUserLimitError(
                            "Package allows 5 users, current org has 8. Remove members before downgrading.",
                          );
                          setUserLimitErrorModalOpen(true);
                          setConfirmModalOpen(false);
                        }}
                        title="Dev mode: Simulate too-many-users error"
                      >
                        Debug
                      </Button>
                    )}
                  </Stack>
                  {isCurrentPlan && (
                    <Alert color="neutral" variant="soft" size="sm">
                      <Typography level="body-sm">
                        This is your current plan configuration. Select a
                        different package or change your modules to continue.
                      </Typography>
                    </Alert>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Confirmation Modal for Affected Workspaces */}
      <Modal open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Confirm Plan Change</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Alert color="warning" variant="soft">
                <Typography level="body-sm">
                  <strong>Warning:</strong> The following workspaces will be
                  paused because they use modules that will be removed from your
                  plan:
                </Typography>
              </Alert>
              <Box>
                <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600 }}>
                  Affected Workspaces ({affectedWorkspaces.length})
                </Typography>
                <Stack spacing={1.5}>
                  {affectedWorkspaces.map((workspace) => (
                    <Card key={workspace.id} variant="outlined" size="sm">
                      <CardContent>
                        <Stack spacing={1}>
                          <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                            {workspace.name}
                          </Typography>
                          <Box>
                            <Typography
                              level="body-xs"
                              color="neutral"
                              sx={{ mb: 0.5 }}
                            >
                              Modules to be removed:
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                              gap={0.5}
                            >
                              {workspace.removed_modules.map((module) => (
                                <Chip
                                  key={module}
                                  size="sm"
                                  variant="soft"
                                  color="warning"
                                >
                                  {moduleRegistry[module]?.name || module}
                                </Chip>
                              ))}
                            </Stack>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
              <Alert color="neutral" variant="soft">
                <Typography level="body-sm">
                  These workspaces will be paused and become read-only. You can
                  re-enable them by upgrading your plan to include the required
                  modules.
                </Typography>
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => {
                setConfirmModalOpen(false);
                setAffectedWorkspaces([]);
              }}
              disabled={changingPlan}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={proceedWithChange}
              loading={changingPlan}
            >
              Confirm & Change Plan
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* User Limit Error Modal */}
      <Modal
        open={userLimitErrorModalOpen}
        onClose={() => setUserLimitErrorModalOpen(false)}
      >
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Cannot Change Plan</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Alert color="danger" variant="soft">
                <Typography level="body-sm">
                  <strong>User Limit Exceeded:</strong> {userLimitError}
                </Typography>
              </Alert>
              <Typography level="body-sm">
                You can manage members in Organization Settings.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => {
                setUserLimitErrorModalOpen(false);
                setUserLimitError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              component={Link}
              to="/organization/settings"
              onClick={() => {
                setUserLimitErrorModalOpen(false);
                setUserLimitError(null);
              }}
            >
              Go to Organization Settings
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Page>
  );
};

export default ChangePlan;
